/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog';

import { ApplicationModes } from '../../../common/constants/application-modes';
import { RovoIcon } from '../../../common/constants/icons/rovo';
import { useAppContext } from '../../../common/ui/app-context';
import { onAdvancedSearchLinkSelected } from '../../../common/utils/quick-find/events/advanced-search-link-selected';
import { onTextEntered } from '../../../common/utils/quick-find/events/text-entered';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import {
	useApplicationMode,
	useBootstrap,
	useFeatures,
	useSearchActions,
} from '../../../controllers/store';
import { useBootstrapActions } from '../../../controllers/store/bootstrap';
import {
	usePostQueryLoading,
	usePreQueryLoading,
	useQuickFindActions,
	useQuickFindOpen,
	useUpdateRecentQueries,
} from '../../../controllers/store/quick-find';

import { LegacySearchIcon } from './legacy-search-icon';
import { messages } from './messages';

const textFieldBoxStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: 'size.300',
	':focus': {
		height: 'size.400',
	},
	border: '1px solid',
	borderColor: 'color.border.input',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: 'border.radius.200',
	paddingRight: 'space.200',
	width: '100%',
});

const textFieldBoxFocusedStyles = xcss({
	height: 'size.400',
	border: 'none',
	boxShadow: 'elevation.shadow.overlay',
	paddingRight: '0',
});

const inputStyles = css({
	outline: 'none',
	background: 'none',
	fontSize: 'font.body',
	color: token('color.text.subtlest'),
	border: 'none',
	width: '100%',
	padding: '0',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',

	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
});

const inputFocusedStyles = css({
	color: token('color.text'),
});

const iconWrapperStyles = xcss({
	minWidth: 'size.300',
	minHeight: 'size.300',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	color: 'color.text.subtlest',
});

const iconWrapperFocusedStyles = xcss({
	marginInlineStart: 'space.200',
	marginInlineEnd: 'space.150',
	minWidth: 'size.200',
});

const clearIconStyles = xcss({
	marginRight: 'space.100',
});

// Nav3 style overrides
// Can be removed once Confluence is migrated to Nav4
const nav3StyleOverrides = {
	textFieldBoxStyles: xcss({
		borderColor: 'color.border.input',
		backgroundColor: 'color.background.input',
		borderRadius: '5px',
	}),
	textFieldBoxFocusedStyles: xcss({
		backgroundColor: 'elevation.surface.overlay',
	}),
	iconWrapperStyles: xcss({
		minWidth: '30px',
	}),
	inputStyles: css({
		font: 'font.body',
		lineHeight: '20px',
	}),
};

export type SearchTextFieldProps = {
	dialogId: string;
	inputRef?: RefObject<HTMLInputElement>;
};

export const SearchTextField = ({ dialogId, inputRef }: SearchTextFieldProps) => {
	const clearIconRef = useRef<HTMLButtonElement>(null);

	const intl = useIntl();

	const { generateSearchUrl, onNavigate, queryParams } = useAppContext();

	const [{ primaryProduct, user, isNav4Enabled }] = useBootstrap();
	const [features] = useFeatures();
	const quickFindOpen = useQuickFindOpen();
	const { getSelectedIndex } = useKeyboardHighlightProvider();
	const preQueryLoading = usePreQueryLoading();
	const postQueryLoading = usePostQueryLoading();
	const updateRecentQueries = useUpdateRecentQueries();
	const applicationMode = useApplicationMode();

	const { setQueryParams } = useBootstrapActions();
	const { setQuickFindOpen } = useQuickFindActions();
	const { setQueryUpdatedTime } = useSearchActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const [localQuery, setLocalQuery] = useState('');

	// Sync local query with input query
	useEffect(() => {
		setQueryParams({ text: localQuery });
	}, [setQueryParams, localQuery]);

	// Update query time when query changes
	useEffect(() => {
		setQueryUpdatedTime();
	}, [setQueryUpdatedTime, localQuery]);

	// Sync search query with local query e.g
	//  - when the page loads with a query in query parameters
	//  - when smart answers suggest follow up queries
	useEffect(() => {
		if (typeof queryParams.text === 'string') {
			setLocalQuery(queryParams.text);
		}
	}, [queryParams, setLocalQuery]);

	useEffect(() => {
		if (quickFindOpen) {
			inputRef?.current?.focus();
		} else {
			inputRef?.current?.blur();
		}
	}, [inputRef, quickFindOpen]);

	const onKeyPress = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				if (!primaryProduct) {
					return;
				}

				// Store search in recent searches
				if (localQuery.trim() !== '' && user && user.id && primaryProduct) {
					addRecentSearch(localQuery.trim(), user.id, primaryProduct, RECENT_SEARCH_KEY);
					updateRecentQueries?.();
				}

				// Don't navigate to the search page if enter is pressed whilst a result
				// is highlighted. The keyboard event is handled by the search result.
				const resultHighlighted = getSelectedIndex && getSelectedIndex() !== undefined;
				if (resultHighlighted) {
					return;
				}

				if (features?.chromeExtension?.enabled) {
					features.chromeExtension.onInputSubmit({ query: localQuery });
				}

				event.currentTarget.blur();
				setQuickFindOpen(false);

				fireAnalyticsEvent(
					onAdvancedSearchLinkSelected({
						primaryProduct,
						attributes: {
							...commonAttributes,
							trigger: 'return',
							newTab: false,
							isLoading: preQueryLoading || postQueryLoading,
						},
						nonPrivacySafeAttributes,
					}),
				);

				if (event.metaKey || event.ctrlKey) {
					onNavigate(
						generateSearchUrl({
							...queryParams,
							product: primaryProduct,
							text: localQuery,
						}),
						'push',
					);
				} else {
					onNavigate(
						generateSearchUrl({
							...queryParams,
							text: localQuery,
						}),
						'push',
					);
				}
			}

			if (event.key === 'Escape') {
				event.currentTarget.blur();
				setQuickFindOpen(false);
			}
		},
		[
			commonAttributes,
			features?.chromeExtension,
			fireAnalyticsEvent,
			generateSearchUrl,
			getSelectedIndex,
			localQuery,
			nonPrivacySafeAttributes,
			onNavigate,
			postQueryLoading,
			preQueryLoading,
			primaryProduct,
			queryParams,
			setQuickFindOpen,
			updateRecentQueries,
			user,
		],
	);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setLocalQuery(event.currentTarget.value);

			fireAnalyticsEvent(
				onTextEntered({
					attributes: {
						...commonAttributes,
						queryUpdatedAgo: 0, // Query just updated
					},
					nonPrivacySafeAttributes: { query: event.currentTarget.value },
				}),
			);
		},
		[commonAttributes, fireAnalyticsEvent],
	);

	return (
		<Box
			xcss={[
				textFieldBoxStyles,
				quickFindOpen && textFieldBoxFocusedStyles,
				!isNav4Enabled && nav3StyleOverrides.textFieldBoxStyles,
				!isNav4Enabled && quickFindOpen && nav3StyleOverrides.textFieldBoxFocusedStyles,
			]}
			onClick={() => {
				inputRef?.current?.focus();
			}}
			aria-controls={quickFindOpen ? dialogId : undefined}
			aria-expanded={quickFindOpen}
			aria-haspopup="true"
		>
			<Box
				xcss={[
					iconWrapperStyles,
					quickFindOpen && iconWrapperFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.iconWrapperStyles,
				]}
			>
				{quickFindOpen &&
				applicationMode === ApplicationModes.Rovo &&
				fg('rovo_search_fy25q2_visual_refresh') ? (
					<RovoIcon />
				) : isNav4Enabled || quickFindOpen ? (
					<SearchIcon color="currentColor" label={intl.formatMessage(messages.searchIconLabel)} />
				) : (
					<LegacySearchIcon isExpanded={quickFindOpen} />
				)}
			</Box>
			<input
				ref={inputRef}
				css={[
					inputStyles,
					quickFindOpen && inputFocusedStyles,
					!isNav4Enabled && nav3StyleOverrides.inputStyles,
				]}
				onFocus={() => setQuickFindOpen(true)}
				onKeyDown={onKeyPress}
				placeholder={intl.formatMessage(
					isNav4Enabled || quickFindOpen
						? messages.inputPlaceholder
						: messages.inputPlaceholderNav3,
				)}
				onChange={onChange}
				value={localQuery}
				data-testid="search-input"
				autoComplete="off"
			/>
			{quickFindOpen && localQuery && (
				<Box xcss={clearIconStyles}>
					<IconButton
						ref={clearIconRef}
						icon={(iconProps) => (
							<CrossIcon
								{...iconProps}
								color={token('color.icon', N500)}
								label={intl.formatMessage(messages.clearIconLabel)}
								LEGACY_size="small"
							/>
						)}
						spacing={'compact'}
						onClick={() => {
							setLocalQuery('');
							inputRef?.current?.focus();
						}}
						label={intl.formatMessage(messages.clearIconLabel)}
						appearance={'subtle'}
					/>
				</Box>
			)}
		</Box>
	);
};
